/**
 * Collapse repeated identical AI notes.
 *
 * The worker re-runs duplicate detection / triage on every sync and appends a
 * new note each time, so the timeline showed the same line five times
 * ("AI duplicate detection: No comparable open tickets found." ×5). One card
 * with a count is enough; the underlying rows are untouched.
 */

export interface CollapsibleNote {
  id: string;
  type: string;
  author: string;
  content: string;
}

export type CollapsedNote<T extends CollapsibleNote> = T & { duplicateCount: number };

function keyOf(note: CollapsibleNote): string {
  return [note.type, note.author, note.content.replace(/\s+/g, " ").trim()].join("|");
}

/**
 * Keep the first (newest) occurrence of each identical AI note and count the
 * repeats. Non-AI notes are returned untouched.
 */
export function collapseDuplicateNotes<T extends CollapsibleNote>(notes: readonly T[]): Array<CollapsedNote<T>> {
  const counts = new Map<string, number>();
  for (const note of notes) {
    if (note.type !== "ai_suggestion") continue;
    const key = keyOf(note);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const seen = new Set<string>();
  const out: Array<CollapsedNote<T>> = [];
  for (const note of notes) {
    if (note.type !== "ai_suggestion") {
      out.push({ ...note, duplicateCount: 1 });
      continue;
    }
    const key = keyOf(note);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...note, duplicateCount: counts.get(key) ?? 1 });
  }
  return out;
}
